/**
 * Language mapping configuration
 * Maps language codes used in i18n to actual folder names in the locales directory
 */

export interface LanguageMapping {
  [key: string]: {
    folder: string;
    name: string;
    nativeName: string;
    backendCode?: string;
  };
}

export const languageMapping: LanguageMapping = {
  'en': {
    folder: 'en',
    name: 'English',
    nativeName: 'English'
  },
  'ar': {
    folder: 'ar-SA',
    name: 'Arabic',
    nativeName: 'العربية'
  },
  'bg': {
    folder: 'bg-BG',
    name: 'Bulgarian',
    nativeName: 'Български'
  },
  'cz': {
    folder: 'cs-CZ',
    name: 'Czech',
    nativeName: 'Čeština',
    backendCode: 'cs'
  },
  'da': {
    folder: 'da-DK',
    name: 'Danish',
    nativeName: 'Dansk'
  },
  'de': {
    folder: 'de-DE',
    name: 'German',
    nativeName: 'Deutsch'
  },
  'gr': {
    folder: 'el-GR',
    name: 'Greek',
    nativeName: 'Ελληνικά',
    backendCode: 'el'
  },
  'es-LA': {
    folder: 'es-419',
    name: 'Spanish (Latin America)',
    nativeName: 'Español (Latinoamérica)'
  },
  'es': {
    folder: 'es-ES',
    name: 'Spanish',
    nativeName: 'Español'
  },
  'fi': {
    folder: 'fi-FI',
    name: 'Finnish',
    nativeName: 'Suomi'
  },
  'fr-CA': {
    folder: 'fr-CA',
    name: 'French (Canada)',
    nativeName: 'Français (Canada)'
  },
  'fr': {
    folder: 'fr-FR',
    name: 'French',
    nativeName: 'Français'
  },
  'he': {
    folder: 'he-IL',
    name: 'Hebrew',
    nativeName: 'עברית'
  },
  'hr': {
    folder: 'hr-HR',
    name: 'Croatian',
    nativeName: 'Hrvatski'
  },
  'hu': {
    folder: 'hu-HU',
    name: 'Hungarian',
    nativeName: 'Magyar'
  },
  'id': {
    folder: 'id-ID',
    name: 'Indonesian',
    nativeName: 'Bahasa Indonesia'
  },
  'it': {
    folder: 'it-IT',
    name: 'Italian',
    nativeName: 'Italiano'
  },
  'ja': {
    folder: 'ja-JP',
    name: 'Japanese',
    nativeName: '日本語'
  },
  'ko': {
    folder: 'ko-KR',
    name: 'Korean',
    nativeName: '한국어'
  },
  'lt': {
    folder: 'lt-LT',
    name: 'Lithuanian',
    nativeName: 'Lietuvių'
  },
  'ms': {
    folder: 'ms-MY',
    name: 'Malay',
    nativeName: 'Bahasa Melayu'
  },
  'nl': {
    folder: 'nl-NL',
    name: 'Dutch',
    nativeName: 'Nederlands'
  },
  'no': {
    folder: 'no-NO',
    name: 'Norwegian',
    nativeName: 'Norsk'
  },
  'pl': {
    folder: 'pl-PL',
    name: 'Polish',
    nativeName: 'Polski'
  },
  'pt-BR': {
    folder: 'pt-BR',
    name: 'Portuguese (Brazil)',
    nativeName: 'Português (Brasil)'
  },
  'pt': {
    folder: 'pt-PT',
    name: 'Portuguese',
    nativeName: 'Português'
  },
  'ro-RO': {
    folder: 'ro-RO',
    name: 'Romanian',
    nativeName: 'Română'
  },
  'ru': {
    folder: 'ru-RU',
    name: 'Russian',
    nativeName: 'Русский'
  },
  'sk': {
    folder: 'sk-SK',
    name: 'Slovak',
    nativeName: 'Slovenčina'
  },
  'sl': {
    folder: 'sl-SI',
    name: 'Slovenian',
    nativeName: 'Slovenščina'
  },
  'sv': {
    folder: 'sv-SE',
    name: 'Swedish',
    nativeName: 'Svenska'
  },
  'th': {
    folder: 'th-TH',
    name: 'Thai',
    nativeName: 'ไทย'
  },
  'tr': {
    folder: 'tr-TR',
    name: 'Turkish',
    nativeName: 'Türkçe'
  },
  'uk': {
    folder: 'uk-UA',
    name: 'Ukrainian',
    nativeName: 'Українська'
  },
  'vi': {
    folder: 'vi-VN',
    name: 'Vietnamese',
    nativeName: 'Tiếng Việt'
  },
  'zh': {
    folder: 'zh-TW',
    name: 'Chinese (Traditional)',
    nativeName: '繁體中文'
  }
};

/**
 * Normalize an incoming language code to the canonical mapping key.
 * - Replaces underscores with dashes
 * - Lowercases the language part, uppercases alpha region (keeps numeric regions)
 * - Maps es-419 to es-la alias
 * - Falls back to base language if region-specific key not present
 */
export const resolveLanguageCode = (languageCode: any): string => {
  if (Array.isArray(languageCode)) {
    languageCode = languageCode[0];
  }
  if (typeof languageCode !== 'string' || !languageCode) {
    return 'en';
  }

  const cleaned = languageCode.replace(/_/g, '-');
  const parts = cleaned.split('-');

  let canonical = parts[0]?.toLowerCase() || '';
  if (parts[1]) {
    const region = parts[1];
    const upperRegion = /^[0-9]+$/.test(region) ? region : region.toUpperCase();
    canonical = `${canonical}-${upperRegion}`;
  }

  // Special alias mapping
  if (canonical.toLowerCase() === 'es-419') {
    return 'es-la';
  }

  if (languageMapping[canonical]) return canonical;

  const base = canonical.split('-')[0];
  if (languageMapping[base]) return base;

  return canonical;
};

/**
 * Get folder name for a language code
 */
export const getLanguageFolder = (languageCode: string): string => {
  const resolved = resolveLanguageCode(languageCode);
  return languageMapping[resolved]?.folder || resolved;
};

/**
 * BCP 47 locale for autosuggest-sdk and similar APIs.
 * Uses the same region as translation folders (e.g. uk → uk-UA, not uk-US).
 * English stays en-US for parity with previous Kayak-style defaults.
 */
export const getAutosuggestLocale = (languageCode: string): string => {
  const folder = getLanguageFolder(languageCode);
  if (folder === 'en') {
    return 'en-US';
  }
  return folder;
};

/**
 * Get all supported language codes
 */
export const getSupportedLanguages = (): string[] => {
  const keys = Object.keys(languageMapping);
  // Add underscore, lowercase aliases for regioned codes (e.g., fr-CA -> fr_ca)
  const underscoreAliases = keys
    .filter(k => k.includes('-'))
    .map(k => k.replace(/-/g, '_').toLowerCase());
  return Array.from(new Set([...keys, ...underscoreAliases]));
};
